import { create } from 'zustand';
import { fetchProducts, getProductBranding, getAllProducts, type ProductBranding } from '@/lib/products';
import { api } from '@/lib/api';
import { edenError } from '@solidafy/api-client';
import type { ResolvedProvider, ProductConnector, ProductPipeline } from '@solidafy/api-client';

export type ProductDashboard = {
  id: string;
  name: string;
  category: string;
  pipeline_id: string | null;
  provider_id: string | null;
  provider_name: string | null;
  template_pipeline_name: string | null;
};

export type SetupStep = {
  name: string;
  type: 'connector' | 'trigger' | 'schedule';
  connector?: string;
  pipeline?: string;
  required?: boolean;
  title: string;
  description: string;
  default?: string;
};

export type ProductIngest = {
  pipeline: string;
  default_schedule: string;
};

export type ProductDashboardDef = {
  name: string;
  category: string;
  description: string;
  requires?: string[];
};

export type PolicyRule = {
  dimension: string;
  operator: string;
  value: string;
};

export type PolicyTemplate = {
  name: string;
  label: string;
  description: string;
  rules: PolicyRule[];
};

export type PolicyDimension = {
  name: string;
  label: string;
  source: string;
  column: string;
};

export type ProductPolicies = {
  seed_table: string;
  dimensions: PolicyDimension[];
  templates: PolicyTemplate[];
  protected_tables: string[];
};

type ProductState = {
  productSlug: string | null;
  branding: ProductBranding | null;
  // New product-based state
  productConnectors: ProductConnector[];
  productPipelines: ProductPipeline[];
  productDisplayName: string;
  productVersion: string;
  productDefaultStartDate: string;
  // Product experience sections
  setupSteps: SetupStep[];
  productIngest: ProductIngest | null;
  dashboardDefs: ProductDashboardDef[];
  productPolicies: ProductPolicies | null;
  // Legacy provider state (backward compatible)
  resolvedProviders: ResolvedProvider[];
  resolving: boolean;
  productDashboards: ProductDashboard[];
  dashboardsLoading: boolean;
  allProducts: ProductBranding[];
  productsLoaded: boolean;

  setProduct: (slug: string) => Promise<void>;
  clearProduct: () => void;
  fetchProductDashboards: (slug: string) => Promise<void>;
  resolveProduct: (slug: string) => Promise<void>;
  loadProducts: () => Promise<void>;
};

/** Read stored product slug — branding is resolved after products are fetched from API */
const storedSlug = localStorage.getItem('product_mode');

export const useProductStore = create<ProductState>()((set, get) => ({
  productSlug: storedSlug,
  branding: null,
  productConnectors: [],
  productPipelines: [],
  productDisplayName: '',
  productVersion: '',
  productDefaultStartDate: '3mo',
  setupSteps: [],
  productIngest: null,
  dashboardDefs: [],
  productPolicies: null,
  resolvedProviders: [],
  resolving: false,
  productDashboards: [],
  dashboardsLoading: false,
  allProducts: getAllProducts(),
  productsLoaded: false,

  loadProducts: async () => {
    const products = await fetchProducts();
    const allProducts = Object.values(products);
    set({ allProducts, productsLoaded: true });

    // Resolve stored product slug now that API data is available
    const currentSlug = get().productSlug;
    if (currentSlug) {
      const branding = getProductBranding(currentSlug);
      if (branding) {
        localStorage.setItem('product_mode', branding.slug);
        set({ productSlug: branding.slug, branding });
      } else {
        // Stored slug doesn't match any known product — clear it
        localStorage.removeItem('product_mode');
        set({ productSlug: null, branding: null });
      }
    }
  },

  setProduct: async (slug) => {
    const branding = getProductBranding(slug);
    if (!branding) {
      return;
    }

    localStorage.setItem('product_mode', branding.slug);
    set({ productSlug: branding.slug, branding });

    // Resolve providers in background
    await get().resolveProduct(branding.slug);
  },

  clearProduct: () => {
    localStorage.removeItem('product_mode');
    set({
      productSlug: null,
      branding: null,
      productConnectors: [],
      productPipelines: [],
      productDisplayName: '',
      productVersion: '',
      productDefaultStartDate: '3mo',
      setupSteps: [],
      productIngest: null,
      dashboardDefs: [],
      productPolicies: null,
      resolvedProviders: [],
      resolving: false,
      productDashboards: [],
      dashboardsLoading: false,
    });
  },

  resolveProduct: async (slug) => {
    set({ resolving: true });
    try {
      const { data, error } = await api.api.v1.products({ slug }).resolve.get();
      if (!error && data) {
        const update: Partial<ProductState> = {};
        if ('connectors' in data) {
          update.productConnectors = data.connectors;
        }
        if ('pipelines' in data) {
          update.productPipelines = data.pipelines;
        }
        if ('display_name' in data) {
          update.productDisplayName = data.display_name;
        }
        if ('version' in data) {
          update.productVersion = data.version;
        }
        if ('default_start_date' in data) {
          update.productDefaultStartDate = data.default_start_date;
        }
        if ('providers' in data) {
          update.resolvedProviders = data.providers;
        }
        if ('setup' in data && data.setup) {
          update.setupSteps = data.setup.steps;
        }
        if ('ingest' in data && data.ingest) {
          update.productIngest = data.ingest;
        }
        if ('dashboards' in data && data.dashboards) {
          update.dashboardDefs = data.dashboards;
        }
        if ('policies' in data && data.policies) {
          update.productPolicies = data.policies;
        }
        set(update);
      }
    } catch {
      // silently fail — pages still render with branding
    } finally {
      set({ resolving: false });
    }
  },

  fetchProductDashboards: async (slug) => {
    set({ dashboardsLoading: true });
    try {
      // Fetch all dashboards and product-specific provider mappings in parallel
      const [allRes, productRes] = await Promise.all([
        api.api.v1.bi.dashboards.get(),
        api.api.v1
          .products({ slug })
          .dashboards.get()
          .catch(() => null),
      ]);

      if (allRes.error) {
        throw new Error(edenError(allRes.error, 'Failed to load dashboards'));
      }
      const allDashboards = allRes.data;

      // Build a lookup from the product endpoint for provider info
      const providerLookup = new Map<
        string,
        {
          pipeline_id: string | null;
          provider_id: string | null;
          provider_name: string | null;
          template_pipeline_name: string | null;
        }
      >();
      if (productRes && !productRes.error) {
        const enriched = productRes.data.dashboards;
        for (const d of enriched) {
          providerLookup.set(d.id, {
            pipeline_id: d.pipeline_id,
            provider_id: d.provider_id,
            provider_name: d.provider_name,
            template_pipeline_name: d.template_pipeline_name,
          });
        }
      }

      set({
        productDashboards: allDashboards.map((d) => {
          const info = providerLookup.get(d.id);
          return {
            id: d.id,
            name: d.name,
            category: d.category || '',
            pipeline_id: info?.pipeline_id ?? null,
            provider_id: info?.provider_id ?? null,
            provider_name: info?.provider_name ?? null,
            template_pipeline_name: info?.template_pipeline_name ?? null,
          };
        }),
      });
    } catch {
      // silently fail — sidebar renders static items, just won't have links
    } finally {
      set({ dashboardsLoading: false });
    }
  },
}));
